import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { useFormatters } from '../../lib/format'
import type { ContractPropertyValue, ExtractionReliability, ExtractionReviewStat } from '../../hooks/useQueries'

// The measured reliability of an extracted clause and the control level it
// calls for (spec 2026-10-27-12 §B, §E). The reference measure answers on day
// one; the organization's own reviews show next to it once they are enough to
// mean something.
export const ORG_REVIEWS_MIN = 20

export type ClauseCheck = { code: string; legal_basis?: string }

// What a value's chip says: an unreviewed extraction takes its tone from the
// field's tier, a value failing a consistency check reads « à vérifier »
// whatever its tier or source.
export type ClauseState = 'approved' | 'extracted' | 'to_review' | 'to_verify'

export function clauseChecks(value?: ContractPropertyValue): ClauseCheck[] {
  return ((value as { checks?: ClauseCheck[] } | undefined)?.checks ?? [])
}

export function extractionState(value: ContractPropertyValue | undefined, reliability?: ExtractionReliability | null): ClauseState | null {
  if (!value) return null
  if (clauseChecks(value).length > 0) return 'to_verify'
  const source = (value.source_details as { source?: string } | undefined)?.source
  if (source !== 'document_extraction') return null
  if (value.approved_at) return 'approved'
  switch (reliability?.tier) {
    case 'high':
      return 'extracted'
    case 'medium':
      return 'to_review'
    default:
      return 'to_verify'
  }
}

export const STATE_VARIANT: Record<ClauseState, 'success' | 'secondary' | 'warning' | 'destructive'> = {
  approved: 'success',
  extracted: 'secondary',
  to_review: 'warning',
  to_verify: 'destructive',
}

// The reliability of one clause: tier and counts, the measure's corpus, model
// and date in a tooltip, and the organization's reviews once there are enough.
export function ClauseReliabilityBadge({ code, reliability, review }: {
  code: string
  reliability: ExtractionReliability
  review?: ExtractionReviewStat
}) {
  const { t } = useTranslation('contracts')
  const { formatDate } = useFormatters()
  const measured = reliability.tier !== 'unmeasured'
  const correct = Math.round(reliability.correct)
  const tooltip = measured
    ? t('clauses.reliability.measured_on', {
        count: reliability.observed,
        model: reliability.model,
        date: reliability.measured_to ? formatDate(reliability.measured_to) : '—',
      })
    : t('clauses.reliability.not_measured', { model: reliability.model || '—' })
  const showReviews = !!review && review.reviews >= ORG_REVIEWS_MIN
  const variant = { high: 'success', medium: 'warning', low: 'destructive', unmeasured: 'secondary' }[reliability.tier] as
    'success' | 'warning' | 'destructive' | 'secondary'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" data-testid={`clause-reliability-${code}`} data-tier={reliability.tier} tabIndex={0}>
          <Badge variant={variant} className="text-[10px] font-normal">
            {t(`clauses.reliability.tier.${reliability.tier}` as any)}
          </Badge>
          {measured && (
            <span>
              {t('clauses.reliability.correct', { correct, filled: reliability.filled })} · {t('clauses.reliability.filled', { filled: reliability.filled, observed: reliability.observed })}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{tooltip}</p>
        <p>{t(`clauses.reliability.control.${reliability.control_level}` as any)}</p>
        {showReviews && (
          <p data-testid={`clause-org-reviews-${code}`}>{t('clauses.reliability.org_reviews', { confirmed: review!.confirmed, count: review!.reviews })}</p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

// The reason a check failed, in words.
export function useCheckReason() {
  const { t } = useTranslation('contracts')
  return (check: ClauseCheck) => t(`clauses.check.${check.code}` as any, { basis: check.legal_basis ?? '' })
}
